import React, { useEffect, useState } from "react";
import styles from "./SiteNav.module.scss";

import useDocumentScrollThrottled from "../../utilities/useDocumentScrollThrottled";
import { useViewport } from "../../hoc/ViewportProvider";
import { useLocale } from "../../i18n/LocaleContext";

import NavButton from "./NavButton/NavButton";
import SideDrawer from "./SideDrawer/SideDrawer";
import AuthButton from "../../components/auth/AuthButton";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";
import { featureFlags } from "../../utilities/featureFlags";

import AmpersandLogo from "../../assets/images/svg/logo-amp.svg?react";

function SiteNav(props) {
  const disableStickyNav = Boolean(props.disableStickyNav);

  //Header
  const [shouldHideHeader, setShouldHideHeader] = useState(false);
  const [shouldShowShadow, setShouldShowShadow] = useState(false);

  //Side drawer
  const [shouldShowSideDrawer, setShouldShowSideDrawer] = useState(false);
  const [suppressNavHover, setSuppressNavHover] = useState(false);

  const { t, locale } = useLocale();
  const prevLocaleRef = React.useRef(locale);

  const MINIMUM_SCROLL = 5;
  // const TIMEOUT_DELAY = 200;

  useEffect(() => {
    if (disableStickyNav) {
      setShouldHideHeader(false);
      setShouldShowShadow(false);
    }
  }, [disableStickyNav]);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    // Native select navigation can leave hover styles "stuck" until the pointer moves.
    if (prevLocaleRef.current === locale) {
      return undefined;
    }
    prevLocaleRef.current = locale;

    setSuppressNavHover(true);
    document.body?.setAttribute("data-nav-suppress-hover", "true");
    document.body?.setAttribute("data-nav-suppress-transition", "true");

    const clearTransitionSuppression = () => {
      document.body?.removeAttribute("data-nav-suppress-transition");
    };

    const raf1 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(clearTransitionSuppression);
    });

    const clearSuppression = () => {
      setSuppressNavHover(false);
      document.body?.removeAttribute("data-nav-suppress-hover");
    };

    const clearOnMove = () => {
      clearSuppression();
      window.removeEventListener("pointermove", clearOnMove);
      window.removeEventListener("mousemove", clearOnMove);
      window.removeEventListener("pointerdown", clearOnDown);
    };

    const clearOnDown = () => {
      clearSuppression();
      window.removeEventListener("pointermove", clearOnMove);
      window.removeEventListener("mousemove", clearOnMove);
      window.removeEventListener("pointerdown", clearOnDown);
    };

    window.addEventListener("pointermove", clearOnMove, { passive: true });
    window.addEventListener("mousemove", clearOnMove, { passive: true });
    window.addEventListener("pointerdown", clearOnDown, { passive: true });

    return () => {
      window.removeEventListener("pointermove", clearOnMove);
      window.removeEventListener("mousemove", clearOnMove);
      window.removeEventListener("pointerdown", clearOnDown);
      document.body?.removeAttribute("data-nav-suppress-hover");
      document.body?.removeAttribute("data-nav-suppress-transition");
      window.cancelAnimationFrame(raf1);
    };
  }, [locale]);

  useDocumentScrollThrottled(
    (callbackData) => {
      const { previousScrollTop, currentScrollTop } = callbackData;
      const isScrolledDown = previousScrollTop < currentScrollTop;
      const isMinimumScrolled = currentScrollTop > MINIMUM_SCROLL;

      setShouldShowShadow(currentScrollTop > 330);

      // setTimeout(() => {
      setShouldHideHeader(() => {
        return isScrolledDown && isMinimumScrolled;
      });
      // }, TIMEOUT_DELAY);
    },
    !disableStickyNav
  );

  const shadowStyle = !disableStickyNav && shouldShowShadow ? styles.Shadow : "";
  const hiddenStyle =
    !disableStickyNav && shouldHideHeader && !shouldShowSideDrawer ? styles.Hidden : "";
  const staticStyle = disableStickyNav ? styles.HeaderBarStatic : "";

  const { width } = useViewport();
  //const breakpoint = 700; collapse buttons on small screens
  const collapseButtons = width < 770;

  const toggleMenu = (e) => {
    setShouldShowSideDrawer((p) => !p);
    e.preventDefault();
  };

  return (
    <header className={styles.SiteNav}>
      <div className={`${styles.HeaderBar} ${hiddenStyle} ${shadowStyle} ${staticStyle}`}>
        <NavButton
          type="menu"
          collapse={collapseButtons}
          buttonClickListener={(e) => toggleMenu(e)}
          sideDrawerOpen={shouldShowSideDrawer}
          suppressHover={suppressNavHover}
        >
          {shouldShowSideDrawer ? t("nav.close") : t("nav.menu")}
        </NavButton>
        <NavButton
          type="donate"
          collapse={collapseButtons}
          buttonClickListener={() => {
            props.openDonationModal();
          }}
          suppressHover={suppressNavHover}
        >
          {t("nav.supportUs")}
        </NavButton>
        {featureFlags.shop && (
          <AuthButton collapse={collapseButtons} suppressHover={suppressNavHover} />
        )}
        <LanguageSwitcher
          className={styles.LanguageSwitcher}
          compact={collapseButtons}
          suppressHover={suppressNavHover}
        />
        {shouldShowShadow && (
          <a href="#top">
            <AmpersandLogo
              className={styles.AmpersandLogo}
              onClick={() => setShouldShowSideDrawer(false)}
            />
          </a>
        )}
      </div>
      <SideDrawer
        show={shouldShowSideDrawer}
        setShouldShowSideDrawer={setShouldShowSideDrawer}
        closeMenu={() => {
          setShouldShowSideDrawer(false);
        }}
        openDonationModal={props.openDonationModal}
      />
    </header>
  );
}

export default SiteNav;
